import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import { styled, useTheme } from '@mui/material/styles';
import { useOktaAuth } from '@okta/okta-react';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'react-string-format';
import { Container } from 'reactstrap';
import { constants } from '../../constants';
import useMyMApi from '../../hooks/useMyMApi';
import useWebContext from '../../hooks/useWebContext';
import { AuthUserContext } from '../../provider/AuthUserContextProvider';
import { MyMVariantButton } from '../../ui-elements/MyMButton';
import { MyMDropDownList } from '../../ui-elements/MyMDropDownList';
import { MyMImage } from '../../ui-elements/MyMImage';
import { MyMLabel, MyMVariantLabel } from '../../ui-elements/MyMLabel';
import Footer from '../MyM902_Footer/Footer';
import { MyMLink } from '../../ui-elements/MyMLink';
import { MyMModal } from '../../ui-elements/MyMModal';
import Help from '../MyM908_Help/Help';
import AppBar from '@mui/material/AppBar';
import Typography from '@mui/material/Typography';
import { useMediaQuery } from '../../hooks/useMediaQuery';


/**
 * Top Page Component
 */
const Top = () => {
  const screenId = 'MyM101';
  const { controlSettings } = useWebContext(screenId);
  const theme = useTheme();
  const apiRequest = useMyMApi(screenId);
  const [userList, setUserList] = useState(null);
  const [selectUserKey, setSelectUserKey] = useState(sessionStorage.getItem(constants.HttpStorageItemKey.DebugUserSsoKey) ?? '');
  const { oktaAuth } = useOktaAuth();
  const navigate = useNavigate();
  const { setAuthUser } = useContext(AuthUserContext);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const handleHelpOpen = () => setIsHelpOpen(true);
  const handleHelpClose = () => setIsHelpOpen(false);
  const isMobile = useMediaQuery('(max-width:499px)');

  useEffect(() => {
    if (process.env.REACT_APP_ALLOW_SELECT_DEBUG_USER === '1') {
      apiRequest.requestJson('api/user-info/debug-user-list', 'GET', (result) => setUserList(result));
    }
  }, []);

  const StyledMain = styled('main')(() => ({
    display: 'flex',
    flexGrow: 1,
    overflowY: 'auto'
  }));

  const LoginContainer = styled(Container)(() => (
    {
      flexFlow: 'column',
      gap: '1rem',
      textAlign: 'center',
      height: '100%',
      alignItems: 'center',
      display: 'flex',
    }))

  const MyMTopImage = styled(MyMImage)(() => (
    {
      zIndex: '-1',
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    }))

  const MyMLoginButton = styled(MyMVariantButton)(() => (
    {
      border: '3px solid #fff',
      borderRadius: '15px',
      fontSize: '1.5rem',
      fontWeight: 'bold',
      padding: '15px 0px',
      width: '65%',
      backgroundColor: theme.palette.background.secondary,
      ':hover': {
        backgroundColor: theme.palette.background.secondary,
      }
    }))

  const onClickLogin = () => {
    sessionStorage.removeItem(constants.HttpStorageItemKey.DebugUserSsoKey)
    if (selectUserKey) {
      setAuthUser(null);
      sessionStorage[constants.HttpStorageItemKey.DebugUserSsoKey] = selectUserKey;
      navigate('/MyM703')
    } else {
      oktaAuth.signInWithRedirect();
    }
  };

  return (
    <>
      {controlSettings.LabelList.lblNotificationMessage && (
        <AppBar
          position="static"
          elevation={0}
          sx={{
            backgroundColor: '#777777',
            color: '#333',
            minHeight: {lg:'53px',xl:'60px'},
            justifyContent: 'center'
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              pl: 3,
              py: 1,
              textAlign: 'left',
              fontSize: '1.1rem',
              color: '#fff',
            }}
          >
            {controlSettings.LabelList.lblNotificationMessage}
          </Typography>
        </AppBar>
      )}
      <StyledMain>
        <Grid container className='h-100'>
          {/* 2024.9.16 MyMine Version2.0対応 Modify Start */}
          <Grid item xs={12} md={8.5} sx={{ padding: { xs: '30px 30px 0px 30px', md: 0 } }}>
            <MyMTopImage screenId={screenId} filePath={controlSettings.ImageList.imgTitle} />
          </Grid>
          <Grid item xs={12} md={3.5}>
            <Stack spacing={3} className='h-100'>
              <Box sx={{ textAlign: 'center', '> img': { width: { md: '350px', xs: '50%' }, height: { md: '160px' } } }}>
                <MyMImage screenId={screenId} filePath={controlSettings.ImageList.imgLogo} />
              </Box>
              <Box sx={{ marginTop: { md: '80px !important', xs: '5px !important' } }}>
                <LoginContainer>
                  <MyMVariantLabel className='fw-bold mb-3' variant='h5'>
                    {controlSettings.LabelList.lblWelcomeMessage}
                  </MyMVariantLabel>
                  {userList &&
                    <FormControl sx={{ m: 1, width: '100%', maxWidth: '350px' }}>
                      <MyMDropDownList
                        needsBlank={true}
                        value={selectUserKey}
                        sx={{ backgroundColor: 'white', textAlign: 'left' }}
                        autoWidth
                        onChange={e => setSelectUserKey(e.target.value)}
                        className='mt-0'
                        options={userList.map((user) => {
                          return {
                            value: user.SsoKey,
                            text: `${user.SsoKey}(${(user.Role ?? '')} - ${user.Status ?? ''} - ${user.Company ?? ''})`,
                          }
                        })} />
                    </FormControl>}
                  <MyMLoginButton variant='contained' onClick={onClickLogin}>
                    {controlSettings.LabelList.btnLogin}
                  </MyMLoginButton>
                  <MyMLabel>
                    {format(controlSettings.LabelList.lblDescription,
                      <Link href={process.env.REACT_APP_HCMID_TOP_URL} target="_blank" sx={{ color: '#00a0e9' }}>
                        {controlSettings.LabelList.lnkRegister}
                      </Link>)}
                  </MyMLabel>
                  <Grid sx={{ padding: { lg: '30px 0', xl: '60px 0' } }}>
                  <MyMLink sx={{ color: '#00a0e9' }} onClick={handleHelpOpen}>
                    <MyMLabel fontSize='23px'>
                    {controlSettings.LabelList.lnkNeedHelp}
                    </MyMLabel>
                    </MyMLink>
                  </Grid>
                </LoginContainer>
              </Box>
              <Grid
                item
                className="d-flex align-items-center justify-content-center"
                sx={{
                  paddingTop: controlSettings.LabelList.lblNotificationMessage ? '15px' : '85px'
                }}
              >
                <a href="https://id.orth-ros.com/" target="_blank" rel="noopener noreferrer">
                  {/* 2026.2.5 HCM-ID Bannerの読み込み不具合対応 Modify Start */}
                  <MyMImage screenId={screenId} filePath={controlSettings.ImageList.imgNinsho} width="280" height="80" />
                  {/* 2026.2.5 HCM-ID Bannerの読み込み不具合対応 Modify End */}
                </a>
              </Grid>

            </Stack>
          </Grid>
          {/* 2024.9.16 MyMine Version2.0対応 Modify End */}
          
        </Grid>
      </StyledMain>
      <MyMModal
        open={isHelpOpen}
        onClose={handleHelpClose}
        height='93%'
        width={isMobile ? '90vw' : 'auto'}
      >
        <Help handleClosePage={handleHelpClose} />
      </MyMModal>
      <Footer />
    </>
  );
}
export default Top;
